import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from "react";
import { Product, ProductContextTypes } from "../types/product.types";
import { getProducts } from "../services/productService";
import { useFilterContext } from "./FilterContext";
import { getFilteredProducts } from "../utils/filters";
import { Package } from "../types/product.types";
import { useOrderContext } from "./OrderContext";
import { usePublicCatalog } from "./PublicCatalogContext";
import { isDateFarEnough, extractGapDays } from "../utils/calendar";
import { getDayHours, isAvailableOn, WEEK_DAY_FULL } from "../utils/calendar";
import { DayKey } from "../types/product.types";
import useCatalogFetch from "../hooks/useCatalogFetch";
import { log } from "../utils/logger";

/**
* React context to provide products data and loading status.
* Supplies both the full and filtered product lists.
*/
const ProductsContext = createContext<ProductContextTypes>({
    products: [],
    packages: [],
    allProducts: [],
    newProducts: [],
    loading: true,
    reloadProducts: () => {}
});

/**
* Provider component to fetch products, manage loading state and filter products based on the FilterContext.
* 
* @param children - React child elements
*/
export const ProductsProvider = ({ children }: { children: React.ReactNode }) => {
    // Filter context
    const { filterData, basicFilter } = useFilterContext();
    // Never changing full product list
    const [ allProducts, setAllProducts ] = useState<Product[]>([]);
    // Products loading flag
    const [loading, setLoading] = useState<boolean>(true)
    const { isPublicView } = usePublicCatalog();

    // Packages
    const [packages, setPackages] = useState<Package[]>([]);
    const { orderData } = useOrderContext();

    const applyProducts = useCallback((data: any[]) => {
        if (!Array.isArray(data)) {
            setAllProducts([]);
            setPackages([]);
            return;
        }
        const typedData = data.map((item: any) => ({
            ...item,
            type: item?.category === "package" ? "package" : "sales",
        }));
        const salesItems = typedData.filter((item: any) => item.type === "sales") as Product[];
        const packageItems = typedData.filter((item: any) => item.type === "package") as Package[];

        log(
            "[Product order] upsell",
            salesItems
                .filter((item) => item.upsell === true)
                .map((item, index) => ({
                    index: index + 1,
                    id: item.id,
                    name: item.name,
                    priority: (item as any).priority ?? null,
                }))
        );
        log(
            "[Product order] new",
            salesItems
                .filter((item) => item.new_product === true)
                .map((item, index) => ({
                    index: index + 1,
                    id: item.id,
                    name: item.name,
                    priority: (item as any).priority ?? null,
                }))
        );

        setAllProducts(salesItems);
        setPackages(packageItems);

        log(
            "[Catalog debug] sales_items split:",
            salesItems.length, "products,",
            packageItems.length, "packages",
            packageItems.map((p: any) => ({
                code: p.code,
                name: p.name,
                category: p.category,
                hasTags: Boolean(p.tags),
                tagGroups: p.tags ? Object.keys(p.tags) : null,
                price_without_tax: p.pricing?.price_without_tax ?? null,
                order_in_advance: p.order_in_advance ?? null,
                weekday: p.weekday ?? null,
            }))
        );
    }, []);

    const { reload: reloadProducts } = useCatalogFetch<Product | Package>({
        fetcher: getProducts,
        onData: applyProducts,
        setLoading,
        logLabel: "product",
    });

    useEffect(() => {
        reloadProducts();
    }, [reloadProducts]);

    // Filter logic
    // Remove the old "newProducts" state+effect.
    // Use memos instead.

    // Common base after fetching:
    const baseAll = useMemo(
        () => [...allProducts, ...packages],
        [allProducts, packages]
    );

    // 1) Compute the shared pipeline once (respects price/occasion/etc., lead time, weekday)
    const eligibleProducts = useMemo(() => {
        // Logs which items each pipeline stage removed, with the data the stage looked at
        const logDropped = (
            stage: string,
            before: (Product | Package)[],
            after: (Product | Package)[],
            detail: (p: any) => Record<string, unknown>
        ) => {
            const kept = new Set(after.map((p) => p.id));
            const dropped = before.filter((p) => !kept.has(p.id));
            if (dropped.length === 0) {
                log(`[Catalog debug] ${stage}: kept all ${after.length}`);
                return;
            }
            log(
                `[Catalog debug] ${stage}: ${before.length} -> ${after.length}, dropped:`,
                dropped.map((p: any) => ({ code: p.code, name: p.name, ...detail(p) }))
            );
        };

        const byFilters = getFilteredProducts(baseAll, filterData);
        logDropped("filters (tags/price)", baseAll, byFilters, (p) => ({
            hasTags: Boolean(p.tags),
            tagGroups: p.tags ? Object.keys(p.tags) : null,
            price_without_tax: p.pricing?.price_without_tax ?? null,
        }));

        if (!isPublicView) {
            const selectedDate = orderData?.selectedDate ?? "";

            const byLeadTime = byFilters.filter((p) => {
                const gap = extractGapDays((p as any).order_in_advance);
                if (gap == null) return true;
                return isDateFarEnough(gap, selectedDate);
            });
            logDropped(`lead time (selectedDate="${selectedDate}")`, byFilters, byLeadTime, (p) => ({
                order_in_advance: p.order_in_advance ?? null,
            }));

            const { day } = getDayHours(orderData.selectedDate);
            const weekday = WEEK_DAY_FULL[day - 1] as DayKey;

            const byWeekday = byLeadTime.filter((p) => isAvailableOn(p, weekday));
            logDropped(`weekday (${weekday})`, byLeadTime, byWeekday, (p) => ({
                weekday: p.weekday ?? null,
            }));

            // sort here for private view
            return byWeekday;
        }

        // Public view: same filters, also sorted by priority
        return byFilters;
    }, [baseAll, filterData, isPublicView, orderData?.selectedDate]);

    // 2) Derive “new” from the already-constrained set
    const newOnlyProducts = useMemo(
        () =>
            eligibleProducts.filter(
                (prod) => prod.type === "sales" && (prod as any).new_product === true
            ),
        [eligibleProducts]
    );

    // 3) Final products depends on basicFilter
    const products = useMemo(
        () => (basicFilter === "new" ? newOnlyProducts : eligibleProducts),
        [basicFilter, newOnlyProducts, eligibleProducts]
    );

    // 4) Expose newProducts so other UIs can show the count/list of new items
    const newProducts = newOnlyProducts;

    return(
        <ProductsContext.Provider value={{products, allProducts, newProducts, loading, packages, reloadProducts}}>
            {children}
        </ProductsContext.Provider>
    )
}

/**
* Hook to access the ProductsContext.
* 
* @returns ProductsContextType object with products data and loading flag
*/
export const useProducts = () => useContext(ProductsContext);
